/**
 * **Parsing** (`/settings/parsing`, engine FR-E38, D19, W12; W28, FR-65, plan
 * D86) — reading a document's details, which happens only when a person or an
 * app asks. Changed on the page and saved by the bar, in the one `PATCH` with
 * the version read:
 *
 * - **Offered in ProDoc's screens** (FR-E3), and **accepted when it
 *   reconciles** (FR-E19).
 * - **The document types ProDoc reads** — the org's view of the profiles,
 *   labelled as the document types are; the plumbing's internal ones never
 *   shown, and an internal name the organisation's list holds kept as saved.
 *   Switching `invoice` off stops credit and debit notes too, and says so (D54).
 *
 * **Never `ai_processing`**: where AI may run, `external` or `india_only`,
 * changes on its own route behind `settings:can_update` **and** step-up, as a
 * retention override does (D13) — the *AI processing* section's *Change…*
 * opens today's dialog, offered only to an administrator whose host can step
 * up. `meta.ai_available: false` (FR-E34) is said: the choice is still stored.
 *
 * **W19 (FR-E70, D200):** where `meta.inhouse_available` is true, *India only* means a
 * document is read by ProDoc's own AI model, which runs in India — and waits for it while it
 * is off; where it is not, it goes to a reviewer, as before. Each environment says its own.
 */

import { ApiError, SETTINGS, errorText, isStepUpCancelled, useLoad, withStepUp, type SettingsAnswer, type StepUpAsk } from '@fiveox/prodoc-ui'
import { Alert, Button, Dialog, RadioGroup, Skeleton, SkeletonRegion, Switch, useToast } from '@fiveox/ui'
import { FileSearch, ScanText, ShieldCheck, Sparkles } from 'lucide-react'
import { useEffect, useState } from 'react'

import { EditBar, EditPage, useLeaveGuard } from '../../components/edit'
import { SettingsSection, SwitchRow } from '../../components/settings-parts'
import { alsoReads, mediaText, parsingOf, profileLabelOf, readableProfiles, type AiProcessing, type ParsingApi, type ParsingSettings as ParsingSection } from '../../lib/parsing'
import { useSettingsHost } from './host'
import { AreaWaiting, SETTINGS_BACK, VERSION_CONFLICT, useSettingsArea } from './settings-data'

/**
 * The two answers to where AI may run (FR-E31, FR-E32), said the same way on
 * the page and in the dialog. `external` is a processor's region, not a promise
 * of residency: Google does not guarantee the parts stay in India.
 */
const AI_CHOICES: Record<AiProcessing, { label: string; description: string }> = {
  external: {
    label: 'External',
    description: 'Google’s AI (Gemini) may help read the parts of a document that need it. Google processes them in its Mumbai region, but does not guarantee they stay in India.',
  },
  india_only: { label: 'India only', description: 'No part of a document is sent to an outside AI service. A document that needs AI goes to a reviewer instead.' },
}

/** W19 (FR-E70): *India only* where ProDoc's own AI model, in India, is configured here. */
const INDIA_ONLY_INHOUSE = 'Read by ProDoc’s own AI model, which runs in India. No part of a document is sent to an outside AI service. While that model is off, a document waits for it.'

/** The two answers as this environment makes them true (W19: `meta.inhouse_available`). */
const aiChoices = (inhouse: boolean): Record<AiProcessing, { label: string; description: string }> =>
  inhouse ? { ...AI_CHOICES, india_only: { label: 'India only', description: INDIA_ONLY_INHOUSE } } : AI_CHOICES

/** What the step-up names before the change is sent. */
const aiStepUpText = (choice: AiProcessing, orgName: string, inhouse: boolean) =>
  choice === 'india_only'
    ? inhouse
      ? `${orgName}’s documents will be read by ProDoc’s own AI model, in India. No part of them will be sent to an outside AI service.`
      : `No part of ${orgName}’s documents will be sent to an outside AI service. One that needs AI goes to a reviewer instead.`
    : `Google’s AI may help read ${orgName}’s documents, in its Mumbai region.`

/** Whether "AI isn’t switched on" is true for this choice here: *India only* read in-house has its model. */
const aiOff = (choice: AiProcessing, aiAvailable: boolean | null, inhouse: boolean) => aiAvailable === false && !(choice === 'india_only' && inhouse)

/** `meta.ai_available: false` (FR-E34): the choice is still stored, for when AI is switched on. */
const AI_OFF = 'AI isn’t switched on for ProDoc yet, so a document that needs it goes to a reviewer whichever you choose.'

/** What the general PATCH may change of the parsing section — never `ai_processing`, which has its own route (D19). */
type ParsingDraft = Pick<ParsingSection, 'offered' | 'profiles_off' | 'auto_accept'>

/* The page's slice of the settings document, and the one key it sends — module-level, so the area's memo holds. */
const draftOf = (s: SettingsAnswer): ParsingDraft => {
  const p = parsingOf(s)
  return { offered: p.offered, profiles_off: [...p.profiles_off], auto_accept: p.auto_accept }
}
const parsingPatch = (d: ParsingDraft) => ({ parsing: { offered: d.offered, profiles_off: d.profiles_off, auto_accept: d.auto_accept } })
/** The switched-off list is a set: the order it was switched in is not a change. */
const sameParsing = (a: ParsingDraft, b: ParsingDraft) => a.offered === b.offered && a.auto_accept === b.auto_accept && JSON.stringify([...a.profiles_off].sort()) === JSON.stringify([...b.profiles_off].sort())

export function ParsingSettings() {
  const { client, parsing, can, orgName, requestStepUp } = useSettingsHost()
  const { toast } = useToast()
  const area = useSettingsArea({ from: draftOf, toPatch: parsingPatch, same: sameParsing })
  const guard = useLeaveGuard(area.dirty)
  const options = useLoad(() => client.documents.options(), [client])
  const profiles = useLoad(() => parsing.profiles(), [parsing])
  const [changingAi, setChangingAi] = useState(false)
  const editable = can(SETTINGS, 'can_update')
  const mayChangeAi = editable && Boolean(requestStepUp)
  const s = area.settings.data
  const draft = area.draft

  const saved = parsingOf(s)
  // What is saved decides the list, so a type switched off and not listed stays to be switched back on.
  const readable = readableProfiles(profiles.data?.items, saved.profiles_off)
  const aiAvailable = profiles.data?.ai_available ?? null
  const inhouse = profiles.data?.inhouse_available === true
  const labelOf = profileLabelOf(options.data, s)
  const ai = aiChoices(inhouse)[saved.ai_processing]

  const set = (next: Partial<ParsingDraft>) => draft && area.setDraft({ ...draft, ...next })
  // Switching a type back on removes it from `profiles_off`; a name the list does not show (an internal profile) stays as it was saved.
  const toggle = (name: string, on: boolean) => draft && set({ profiles_off: on ? draft.profiles_off.filter((n) => n !== name) : [...draft.profiles_off.filter((n) => n !== name), name] })

  const save = () => void area.save().then((ok) => ok && toast('Settings saved.'))

  return (
    <EditPage back={SETTINGS_BACK} title="Parsing" description="Reading a document’s details — only when a person or an app asks for it. Storing a document never waits for it.">
      {guard}
      {!s || !draft ? (
        <AreaWaiting settings={area.settings} label="Loading the parsing settings" heights={['h-44', 'h-64', 'h-32']} />
      ) : (
        <>
          <SettingsSection
            id="screens"
            icon={<ScanText size={18} aria-hidden />}
            title="In ProDoc’s screens"
            description="Whether a person may ask ProDoc to read a document, and whether a reading that reconciles still waits for a reviewer."
          >
            <div className="divide-border divide-y">
              <SwitchRow
                title="Offer parsing in ProDoc’s screens"
                hint="Off, nobody can ask ProDoc to read a document from its screens. An app that keeps its documents in ProDoc still may."
                checked={draft.offered}
                disabled={!editable}
                onChange={(offered) => set({ offered })}
              />
              <SwitchRow
                title="Accept extractions automatically when they reconcile"
                hint="Off, a reviewer accepts each one. One that does not reconcile always waits for a reviewer; an app that reviews in its own screen decides for itself."
                checked={draft.auto_accept}
                disabled={!editable}
                onChange={(auto_accept) => set({ auto_accept })}
              />
            </div>
          </SettingsSection>

          <SettingsSection
            id="types-read"
            icon={<FileSearch size={18} aria-hidden />}
            title="Document types ProDoc reads"
            description="A type switched off is read for no one — not in ProDoc’s screens, not for an app."
          >
            {profiles.loading && !profiles.data ? (
              <SkeletonRegion label="Listing the document types ProDoc reads" className="space-y-2">
                <Skeleton className="h-5 w-64" />
                <Skeleton className="h-5 w-56" />
              </SkeletonRegion>
            ) : profiles.error && !profiles.data ? (
              <p className="text-muted-foreground text-sm">
                Which document types ProDoc reads could not be listed: {profiles.error}{' '}
                <Button variant="link" onClick={() => void profiles.reload()}>
                  Try again
                </Button>
              </p>
            ) : readable.length === 0 ? (
              <p className="text-muted-foreground text-sm">ProDoc does not read any document type yet.</p>
            ) : (
              <ul className="divide-border divide-y" aria-label="Document types ProDoc reads">
                {readable.map((p) => (
                  <li key={p.name} data-profile={p.name} className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
                    <div className="min-w-0">
                      <p className="text-foreground text-sm font-medium">{labelOf(p.name)}</p>
                      <p className="text-muted-foreground text-xs">
                        <span className="font-mono">{p.name}</span>
                        {p.media_types.length ? ` · reads ${mediaText(p.media_types)}` : ''}
                        {/* Engine W17 (D54): switching `invoice` off stops credit and debit notes too — said here. */}
                        {alsoReads(p.name).length ? ` · also for ${alsoReads(p.name).map((t) => labelOf(t).toLowerCase()).join(' and ')} documents` : ''}
                      </p>
                    </div>
                    <Switch
                      checked={!draft.profiles_off.includes(p.name)}
                      disabled={!editable}
                      srLabel={labelOf(p.name)}
                      onChange={(e) => toggle(p.name, e.target.checked)}
                      className="shrink-0"
                    />
                  </li>
                ))}
              </ul>
            )}
          </SettingsSection>

          <SettingsSection
            id="ai"
            icon={<Sparkles size={18} aria-hidden />}
            title="AI processing"
            description="Where AI may help read a document. Changed only by an administrator, who re-verifies to do it."
            action={
              mayChangeAi ? (
                <Button variant="outline" size="sm" aria-label="Change AI processing" onClick={() => setChangingAi(true)}>
                  <ShieldCheck size={15} aria-hidden />
                  Change…
                </Button>
              ) : undefined
            }
          >
            <p className="text-muted-foreground text-sm" data-ai={saved.ai_processing}>
              <span className="text-foreground font-medium">{ai.label}</span> — {ai.description}
            </p>
            {aiOff(saved.ai_processing, aiAvailable, inhouse) && (
              <Alert tone="info" className="mt-4">
                {AI_OFF}
              </Alert>
            )}
          </SettingsSection>

          <EditBar dirty={area.dirty} saving={area.saving} onSave={save} onDiscard={area.discard} problem={area.problem} editable={editable} />

          {mayChangeAi && requestStepUp && (
            <AiProcessingDialog
              open={changingAi}
              current={saved.ai_processing}
              version={s.version}
              aiAvailable={aiAvailable}
              inhouse={inhouse}
              orgName={orgName}
              api={parsing}
              requestStepUp={requestStepUp}
              onClose={() => setChangingAi(false)}
              onConflict={() => void area.settings.reload()}
              onDone={(next) => {
                // The PUT's answer need not carry the floor table in the page's shape; keep the one read.
                if (next) area.settings.set({ ...next, floors: s.floors })
                else void area.settings.reload()
                setChangingAi(false)
                toast('AI processing saved.')
              }}
            />
          )}
        </>
      )}
    </EditPage>
  )
}

/**
 * AI processing (FR-E31, D13): `settings:can_update` **and** step-up, asked
 * up front, exactly as a retention override is — the dialog stands aside
 * while the re-verification is open, its choice kept, and the assertion goes
 * on the one PUT it was minted for. The PUT names the settings version read;
 * a stale one is refused and the settings read again, as the PATCH's is.
 */
function AiProcessingDialog({
  open,
  current,
  version,
  aiAvailable,
  inhouse,
  orgName,
  api,
  requestStepUp,
  onClose,
  onConflict,
  onDone,
}: {
  open: boolean
  current: AiProcessing
  version: number
  aiAvailable: boolean | null
  /** W19: ProDoc's own AI model, in India, is configured here (`meta.inhouse_available`). */
  inhouse: boolean
  orgName: string
  api: ParsingApi
  requestStepUp: StepUpAsk
  onClose: () => void
  /** The version sent was stale: read the settings again. */
  onConflict: () => void
  onDone: (settings: SettingsAnswer | null) => void
}) {
  const [choice, setChoice] = useState<AiProcessing>(current)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    if (!open) return
    setChoice(current)
    setBusy(false)
    setError('')
    setHidden(false)
    // Reset on opening only: a re-read after a conflict must not wipe the choice or the sentence saying why.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const save = async () => {
    setBusy(true)
    setError('')
    try {
      const next = await withStepUp(
        (assertion) => api.setAiProcessing({ ai_processing: choice, version }, { stepUpAssertion: assertion }),
        async (request) => {
          // The step-up dialog stands alone while it is open; this one waits, its choice kept.
          setHidden(true)
          try {
            return await requestStepUp(request)
          } finally {
            setHidden(false)
          }
        },
        { title: 'Change AI processing', what: aiStepUpText(choice, orgName, inhouse) },
        true,
      )
      onDone(next)
    } catch (e) {
      if (e instanceof ApiError && e.reason === 'version_conflict') {
        setError(VERSION_CONFLICT)
        onConflict()
      } else {
        setError(isStepUpCancelled(e) ? 'Nothing was changed — the re-verification was cancelled.' : errorText(e))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open && !hidden} onClose={onClose} title="AI processing" error={error || null}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (!busy && choice !== current) void save()
        }}
      >
        <RadioGroup
          legend="Where AI may help read a document"
          options={(['external', 'india_only'] as const).map((value) => ({
            value,
            label: value === 'external' ? `${aiChoices(inhouse)[value].label} (the default)` : aiChoices(inhouse)[value].label,
            description: aiChoices(inhouse)[value].description,
          }))}
          value={choice}
          onChange={setChoice}
        />
        {aiOff(choice, aiAvailable, inhouse) && <Alert tone="info">{AI_OFF}</Alert>}
        <p className="text-muted-foreground text-xs">It applies to every parse that has not started yet, those already waiting included. Saving asks you to re-verify.</p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" loading={busy} loadingText="Saving…" disabled={choice === current}>
            Re-verify and save
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
